// ============================================
// src/i18n/en/simulators.ts
// ============================================

const simulators_en = {
  name: 'Simulators',
  
  controls: {
    register: 'Register Device',
    start: 'Start Simulation',
    stop: 'Stop Simulation',
    running: 'Running...',
    clear: 'Clear Data',
    heartbeat_send: 'Send Heartbeat',
    heartbeat_start: 'Start Auto Heartbeat',
    heartbeat_stop: 'Stop Auto Heartbeat',
    registered_success: 'Device registered successfully',
    heartbeat_active: 'Auto heartbeat active',
    heartbeat_every: 'every 30s',
  },
  
  config: {
    title: 'Configuration',
    api_url: 'API URL',
    hotel: 'Property',
    hotel_select: 'Select a property',
    device_id: 'Device ID',
    device_id_helper: 'Unique identifier for this device',
    session_count: 'Number of Sessions',
    error_rate: 'Error Rate (%)',
    error_rate_helper: 'Percentage of sessions with errors',
    abandonment_rate: 'Abandonment Rate (%)',
    abandonment_rate_helper: 'Percentage of abandoned sessions',
    delay_steps: 'Delay Between Steps (ms)',
    delay_sessions: 'Delay Between Sessions (ms)',
    select_hotel_first: 'Please select a property first',
    error_loading_devices: 'Error loading devices',
    use_custom_device: 'Use custom device ID (manual entry)',
    device_id_help: 'Enter the installation code (e.g., KR250101.1)',
    select_device: 'Select Device',
    select_device_placeholder: 'Choose a device...',
    loading_devices: 'Loading devices...',
    no_devices: 'No devices available',
    no_devices_in_hotel: 'This property has no registered devices. Enable "Use custom device ID" to manually enter one, or register a device first.',
  },
  
  stats: {
    title: 'Statistics',
    total: 'Total Sessions',
    successful: 'Successful',
    failed: 'Failed',
    abandoned: 'Abandoned',
    average_time: 'Average Time',
    events: 'Total Events',
  },
  
  log: {
    title: 'Event Log',
    no_logs: 'No events yet',
    search_placeholder: 'Search logs...',
    filter: {
      all: 'All',
      info: 'Info',
      success: 'Success',
      warning: 'Warning',
      error: 'Error',
    },
  },
  
  index: {
    title: 'Simulators',
    subtitle: 'Select a simulator to test sending events to the system',
    kiosk: {
      title: 'Kiosk',
      description: 'Simulate the complete check-in process on a physical kiosk with key dispenser',
    },
    dispenser: {
      title: 'Dispenser',
      description: 'Simulate only the physical key dispenser',
    },
    mobile_desk: {
      title: 'Mobile Desk',
      description: 'Simulate check-in from staff mobile device at reception desk',
    },
    mobile_kiosk: {
      title: 'Mobile Kiosk',
      description: 'Simulate check-in from mobile or physical terminal',
    },
    online_checkin: {
      title: 'Online Check-in',
      description: 'Simulate online pre check-in from property website',
    },
    open_button: 'Open',
    coming_soon: 'Coming Soon',
  },
  
  kiosk: {
    title: 'Kiosk Simulator',
    description: 'Simulate the complete check-in process on a physical kiosk',
    enhanced_description: 'Advanced kiosk simulator with journey correlation and enhanced analytics',
    journey_type: {
      label: 'Journey Type',
      new_checkin: 'New Check-in',
      continue_precheckin: 'Continue Pre Check-in',
      walking_guest: 'Walking Guest',
      hybrid_abandonment: 'Hybrid Abandonment',
    },
    guest_config: {
      label: 'Guest Configuration',
      multiple_reservations: 'Has Multiple Reservations',
      room_preassigned: 'Room Pre-assigned',
    },
    guest_type: {
      label: 'Guest Type',
      reservation: 'With Reservation',
      walking: 'Walk-in',
    },
    checkin_type: {
      label: 'Check-in Type',
      reservation: 'With Reservation',
      walking: 'Walk-in',
    },
    key_delivery: {
      label: 'Key Delivery Method',
      physical: 'Physical Key (Dispenser)',
      digital: 'Digital Key (Mobile)',
      qr_dispenser: 'QR for Dispenser',
      manual: 'Manual Delivery (Staff)',
      magnetic_card: 'Magnetic Card',
      rfid_card: 'RFID Card',
      conventional_key: 'Conventional Key',
      digital_pin: 'Digital PIN',
      digital_mobile: 'Digital Mobile',
    },
    sections: {
      to_process: 'Sections to Process',
      search: 'Search Reservation',
      room_selection: 'Room Selection', 
      identification: 'Guest Identification',
      signature: 'Digital Signature',
      upselling: 'Upselling',
      payment: 'Payment',
    },
    stats: {
      reservations: 'With Reservation',
      walking: 'Walk-in',
      continued_precheckin: 'Continued Pre Check-in',
      keys_dispensed: 'Keys Dispensed',
      key_errors: 'Dispenser Errors',
      correlatable: 'Correlatable Journeys',
    },
  },

  dispenser: {
    title: 'Dispenser Simulator',
    description: 'Simulate only the physical key dispenser',
    qr_type: {
      label: 'QR Code Type',
      reservation: 'Reservation Number',
      room: 'Room Number',
    },
    checkin_source: {
      label: 'Check-in Source',
      precheckin: 'Pre Check-in',
      kiosk: 'Kiosk',
      dispenser: 'Dispenser Only',
    },
    dispenser_unit: 'Dispenser Unit',
    perform_pms_checkin: 'Perform check-in in the reservation system',
    stats: {
      mech_jam: 'Mechanical Jams',
      out_of_keys: 'Out of Keys',
      sensor_error: 'Sensor Errors',
      from_precheckin: 'From Pre Check-in',
      from_kiosk: 'From Kiosk',
    },
  },

  mobile_desk: {
    title: 'Mobile Desk Simulator',
    description: 'Simulate check-in from staff mobile device',
    receptionist_name: 'Receptionist Name',
    checkin_type: {
      label: 'Check-in Type',
      reservation: 'With Reservation',
      walking: 'Walk-in',
    },
    key_delivery: {
      label: 'Key Delivery Method',
      digital: 'Digital Key',
      qr: 'QR Code',
      physical: 'Physical Key',
      manual: 'Manual Delivery',
    },
    document_scan: {
      label: 'Document Scan Method',
      auto: 'Auto OCR',
      manual: 'Manual Entry',
      photo: 'Photo Only',
    },
    stats: {
      reservations: 'With Reservation',
      walking: 'Walk-in',
      payment_success: 'Payment Success',
      keys_delivered: 'Keys Delivered',
      digital_keys: 'Digital Keys',
      qr_dispenser: 'QR Dispenser',
      manual_keys: 'Manual Keys',
      physical_keys: 'Physical Keys',
      ocr_scans: 'OCR Scans',
      manual_entry: 'Manual Entry',
    },
  },

  mobile_kiosk: {
    title: 'Mobile Kiosk Simulator',
    description: 'Simulate self-service check-in on mobile or physical terminal',
    terminal_type: {
      label: 'Terminal Type',
      mobile: 'Mobile Terminal',
      physical: 'Physical Terminal',
    },
    checkin_type: {
      label: 'Check-in Type',
      reservation: 'With Reservation',
      walking: 'Walk-in',
    },
    mobile_key: {
      label: 'Mobile Key Method',
      code: 'Access Code',
      app: 'Mobile App',
      qr: 'QR Code',
      manual: 'Manual Delivery',
    },
    physical_key: {
      label: 'Physical Key Method',
      physical: 'Physical Key',
      digital: 'Digital Key',
      manual: 'Manual Delivery',
    },
    info: {
      mobile: 'Mobile terminal for staff-assisted check-in with flexible key delivery options',
      physical: 'Physical kiosk terminal for self-service check-in similar to traditional kiosk',
    },
    stats: {
      reservations: 'With Reservation',
      walking: 'Walk-in',
      keys_delivered: 'Keys Delivered',
      mobile_keys: 'Mobile Keys',
      physical_keys: 'Physical Keys',
      from_mobile: 'Mobile Terminal',
      from_physical: 'Physical Terminal',
      digital_codes: 'Digital Codes',
      qr_dispenser: 'QR for Dispenser',
    },
  },

  online_checkin: {
    title: 'Online Check-in Simulator',
    description: 'Simulate pre check-in from property website',
    key_delivery: {
      label: 'Key Delivery Method',
    },
    digital_key_method: {
      label: 'Digital Key Method',
      mobile: 'Mobile App',
      sms: 'SMS',
      email: 'Email',
      none: 'None',
    },
    link_access: {
      label: 'Link Access Method',
      email: 'Email Link',
      sms: 'SMS Link',
      direct: 'Direct Access',
    },
    digital_key_rate: 'Digital Key Rate (%)',
    stats: {
      digital_keys: 'Digital Keys Sent',
      payment_success: 'Payment Success',
      payment_error: 'Payment Errors',
      from_email: 'From Email',
      from_sms: 'From SMS',
      from_direct: 'Direct Access',
    },
  },

  journey: {
    title: 'Journey Visualization',
    no_journey: 'No active journey',
    phase_precheckin: 'Pre-Check-In',
    phase_checkin: 'Check-In',
    phase_key: 'Key Delivery',
    journey_id: 'Journey ID',
    guest_type: 'Guest Type',
    reservation: 'Reservation',
    room: 'Room',
    correlation_status: 'Correlation Status',
    correlatable: 'Correlatable',
    not_correlatable: 'Not Correlatable',
    correlation_window: 'Correlation Window',
    correlation_method: 'Correlation Method',
    section_s: 'Search Reservation',
    section_r: 'Room Selection',
    section_i: 'Guest Identification',
    section_f: 'Digital Signature',
    section_u: 'Upselling',
    section_p: 'Payment',
    section_k: 'Key Delivery',
    status_not_started: 'Not Started',
    status_in_progress: 'In Progress',
    status_completed: 'Completed',
    status_failed: 'Failed',
    status_abandoned: 'Abandoned',
  },
  
  wizard: {
    step_device: 'Select Device',
    step_config: 'Configure',
    step_run: 'Run Test',
    select_hotel: 'Select Property',
    select_hotel_desc: 'Choose the property where the device is installed',
    select_device: 'Select Device',
    select_device_desc: 'Choose the device to use for this test',
    ready_title: 'Ready to Run',
    ready_desc: 'Everything is configured. Press the button below to start the test.',
    ready_summary: 'The device will be automatically connected and the test will begin.',
    cancel: 'Cancel',
    new_test: 'New Test',
    activity_log: 'Activity Log',
    result_success: 'Test completed successfully',
    result_failed: 'Test completed with errors',
    result_duration: 'Duration: %{duration}s',
    result_events: '%{count} event sent |||| %{count} events sent',
    phase_connecting: 'Connecting device',
    phase_running: 'Running test',
    phase_completing: 'Completing',
    run_test: 'Run Test',
    running_test: 'Running test...',
    api_log_title: 'API Requests',
    api_log_count: '%{count} request captured |||| %{count} requests captured',
    api_log_export: 'Export',
    api_log_live: 'Requests appear in real time',
  },

  shared: {
    reservation_number: {
      label: 'Reservation Number',
      auto_generate: 'Auto Generate',
      manual: 'Manual (same number for combined journey)',
      placeholder: 'Ex: RES001, BKNG12345',
      tooltip: 'Use the same number across different simulators to create combined journeys (ex: pre-check-in + key dispenser)',
      generate_new: 'Generate new number',
    },
  },
};

export default simulators_en;